import { faLock, faLockOpen, faPen, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { Spinner } from '@shared/components/ui/Spinner';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useBatch, useBatchStock, useBatchTraceability } from '../../../hooks/useBatch';
import BatchesModals, { type BatchesModalsRef } from '../../../List/components/modals/BatchesModals';
import { BatchInfo } from '../display/BatchInfo';
import { Breadcrumb } from '../display/Breadcrumb';
import { ProductStrategy } from '../display/ProductStrategy';
import { StockByLocation } from '../display/StockByLocation';
import { Traceability } from '../display/Traceability';
import { getBatchActions } from '../../../models/batchActions';

export const DetailContainer = () => {
  const { t } = useTranslation('batchesModule');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const batchId = id ? parseInt(id, 10) : 0;
  const modalsRef = useRef<BatchesModalsRef>(null);

  const { data: batch, isLoading, error } = useBatch(batchId);
  const { data: stock = [], isLoading: isLoadingStock } = useBatchStock(batchId);
  const { data: events = [], isLoading: isLoadingTraceability } = useBatchTraceability(batchId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full min-h-[calc(100vh-200px)]">
        <Spinner size="md" />
      </div>
    );
  }

  if (error || !batch) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] gap-4">
        <p className="text-sm text-error">Error al cargar el lote</p>
        <Button variant="outline" onClick={() => navigate('/batches')}>
          {t('detail.back')}
        </Button>
      </div>
    );
  }

  const actions = getBatchActions(batch);

  return (
    <>
      <Breadcrumb batch={batch} />
      <div className="flex justify-end gap-2 absolute top-2 right-6">
        {actions.canUnblock && (
          <Button
            variant="outline"
            size="icon"
            onClick={() => modalsRef.current?.openUnblock(batch)}
            className="bg-card-bg"
          >
            <FontAwesomeIcon icon={faLockOpen} className="!w-3 !h-3" />
          </Button>
        )}
        {actions.canBlock && (
          <Button
            variant="outline"
            size="icon"
            onClick={() => modalsRef.current?.openBlock(batch)}
            className="bg-card-bg"
          >
            <FontAwesomeIcon icon={faLock} className="!w-3 !h-3" />
          </Button>
        )}
        {actions.canEdit && (
          <Button
            variant="outline"
            size="icon"
            onClick={() => modalsRef.current?.openEdit(batch)}
            className="bg-card-bg"
          >
            <FontAwesomeIcon icon={faPen} className="!w-3 !h-3" />
          </Button>
        )}
        {actions.canDelete && (
          <Button
            variant="outline"
            size="icon"
            onClick={() => modalsRef.current?.openDelete(batch)}
            className="bg-card-bg text-error"
          >
            <FontAwesomeIcon icon={faTrash} className="!w-3 !h-3" />
          </Button>
        )}
      </div>
      <div className="py-4 px-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <BatchInfo batch={batch} />
          <ProductStrategy product={batch.product} />
        </div>

        <StockByLocation stock={stock} isLoading={isLoadingStock} />
        <Traceability events={events} isLoading={isLoadingTraceability} />

        <BatchesModals ref={modalsRef} />
      </div>
    </>
  );
};
